/* eslint-disable camelcase */
import { FormElement, IFormElementProperties, createFormElement } from '@vfde-brix/ws10/form-element';
import { formValidation } from '@vfde-brix/ws10/validation';
import { createButton } from '@vfde-brix/ws10/button';
import * as mapDispatch from '../action';
import { IInputConfiguration } from '../interface';
import {
    mountFormControlInput,
    separateHouseNumber,
    submitValidator,
    transformContactMethod,
} from './customInputValidator.helper';
import { getCMSConfig } from '../../../helpers/windowObject.helper';
import { mountCaptchaElements } from './createCaptcha.helper';
import { CaptchaId, LmsInputId } from '../../../enums/elementId';
import validatorConfig from '../../../utils/validator';

const markMandatoryInput = (input: FormElement) => {
    const inputProps: IFormElementProperties = input.getProperties();

    input.update({
        ...inputProps,
        stdLabel: inputProps?.stdLabel + ' <span class="text-primary-100">*</span>',
    });
};

const mountFormControlInputElements = (action: typeof mapDispatch) => {
    const configArray = [{
        // privacy policy checkbox
        wrapperId: LmsInputId.ID_WRAPPER_INPUT_CONSENT_PRIVACY_POLICY,
        onChange: (value: string) => action.changeInput({ privacy_notice_consent: !!value }),
        validation: true,
        txtContent: getCMSConfig().consent.privacyNotice.text,
    }, {
        // contact method
        wrapperId: LmsInputId.ID_WRAPPER_INPUT_CONTACT_METHOD,
        onChange: (value: string) => action.changeInput({ next_action: transformContactMethod(value) }),
    }, {
        // title / gender
        wrapperId: LmsInputId.ID_WRAPPER_INPUT_GENDER,
        onChange: (value: string) => action.changeInput({ role: value }),
    }];

    for (const config of configArray) {
        const { wrapperId, onChange, validation, txtContent } = config;

        mountFormControlInput(wrapperId, onChange, validation, txtContent);
    }
};

export const mountLmsForm = (action: typeof mapDispatch) => {
    const formFields: FormElement[] = [];
    const {
        required,
        onlyNumbers,
        secureValue,
        email,
        maxLengthCity,
        maxLengthStreet,
        maxLengthHouse,
        maxLengthZip,
        maxLengthMobile,
        maxLength70,
        maxLength800,
        minLengthZip,
        minLengthMobile,
    } = validatorConfig;

    const inputConfigurations: IInputConfiguration[] = [{
        id: LmsInputId.ID_WRAPPER_INPUT_FIRSTNAME,
        onChange: (_: Event, value: string) => action.changeInput({ givenName: value }),
        validation: [ required, secureValue, maxLength70 ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_LASTNAME,
        onChange: (_: Event, value: string) => action.changeInput({ familyName: value }),
        validation: [ required, secureValue, maxLength70 ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_CONTACT_NUMBER,
        onChange: (_: Event, value: string) => action.changeInput({ phoneNumber: value }),
        validation: [ required, secureValue, onlyNumbers, minLengthMobile, maxLengthMobile ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_EMAIL,
        onChange: (_: Event, value: string) => action.changeInput({ emailAddress: value }),
        validation: [ required, secureValue, email ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_STREET,
        onChange: (_: Event, value: string) => action.changeInput({ streetName: value }),
        validation: [ required, secureValue, maxLengthStreet ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_HOUSENUMBER,
        onChange: (_: Event, value: string) => {
            const { streetNr, streetSuffix } = separateHouseNumber(value);
            action.changeInput({ streetNr, streetSuffix });
        },
        validation: [ required, secureValue, maxLengthHouse ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_ZIP,
        onChange: (_: Event, value: string) => action.changeInput({ postcode: value }),
        validation: [ required, secureValue, minLengthZip, maxLengthZip, onlyNumbers ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_CITY,
        onChange: (_: Event, value: string) => action.changeInput({ city: value }),
        validation: [ required, secureValue, maxLengthCity ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_PRODUCT_INTEREST,
        onChange: (_: Event, value: string) => action.changeInput({ productInterest: value }),
        validation: [ secureValue, maxLength70 ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_NOTES,
        onChange: (_: Event, value: string) => action.changeInput({ description: value }),
        validation: [ secureValue, maxLength800 ],
    }, {
        id: LmsInputId.ID_WRAPPER_INPUT_ORIGINAL_SALES_UNIT,
        onChange: (_: Event, value: string) => action.changeInput({ original_sales_unit: value }),
        validation: [ secureValue, maxLength70 ],
    }, {
        id: CaptchaId.CONTAINER_CAPTCHA_RESULT,
        onChange: (_: Event, value: string) => action.changeInput({ captchaAnswer: value }),
        validation: [ required, secureValue, maxLength70 ],
    }];

    for (const config of inputConfigurations) {
        const { id, onChange, validation } = config;
        const htmlInputElement = document.getElementById(id);

        if (htmlInputElement && onChange) {
            const formElement = createFormElement(htmlInputElement, { onChange, validation });

            validation?.includes(required) && markMandatoryInput(formElement);
            formFields.push(formElement);
        }
    }

    const submitButtonElement = document.getElementById(LmsInputId.ID_WRAPPER_INPUT_SUBMIT);
    const submitButton = submitButtonElement
        ? createButton(submitButtonElement, { onClick: () => submitValidator(action) })
        : undefined;
    const formToValidate = { formFields, submitButton };

    formValidation(formToValidate, false);
    mountFormControlInputElements(action);

    return {
        submitButton,
        formToValidate,
        ...mountCaptchaElements(action),
    };
};
